/**
 * @file src/shell/PenLayer.js
 * LỚP VẼ TỰ DO — canvas 2D nổi TRÊN sân khấu 3D, bật/tắt bởi nút "Bút" ở Header.
 *
 * PHẠM VI: vẽ ĐÈ lên khung nhìn để ghi chú tại chỗ (khoanh vùng, gạch chân, mũi tên
 * tay) — KHÔNG PHẢI vẽ LÊN mô hình. Không có texture 3D nào bị chạm tới; đây là một
 * <canvas> 2D bình thường xếp trên cùng, hoàn toàn tách khỏi WebGL.
 *
 * VÌ SAO KHOÁ ĐƯỢC OrbitControls MÀ KHÔNG ĐỘNG GÌ TỚI Engine: canvas vẽ là LỚP TRÊN
 * CÙNG trong .ol-stage (z-index cao hơn cả canvas WebGL và nhãn CSS2D — xem
 * --ol-z-pen). Bật lên thì `pointer-events: auto`: mọi chuột/chạm dừng lại ở lớp này,
 * không lọt xuống canvas WebGL nên OrbitControls tự nhiên không nhận được sự kiện nào
 * — không cần gọi `controls.enabled = false`. Tắt đi thì `pointer-events: none`,
 * chuột lọt thẳng xuống dưới, xoay/zoom mô hình lại bình thường ngay.
 *
 * Tắt bút (bấm nút "Bút" lần nữa) XOÁ SẠCH nét vẽ luôn — đây là lựa chọn của người
 * dùng (không phải chỉ ngừng nhận chuột như thiết kế ban đầu): bật lại "Bút" luôn là
 * một trang trắng mới, không phải tiếp tục nét cũ. "Xoá" ở bảng màu vẫn giữ để xoá
 * NGAY trong lúc đang vẽ, không cần tắt/mở lại.
 *
 * Lõi vẽ (canvas DPI-aware + pointer + bảng màu) nằm ở DrawCanvas.js, DÙNG CHUNG với
 * Whiteboard.js — xem file đó để biết vì sao tách ra.
 *
 * BẢNG MÀU nằm NGAY TRONG HEADER, cùng dòng với nút "Bút" — KHÔNG còn nổi ở góc
 * trên-phải sân khấu như bản trước (chèn lên ToolRail, đóng cùng góc đó, xem lịch sử
 * review "phần bút bị chèn"). Đứng NGAY TRƯỚC nhóm nút hành động (Bút/Bảng
 * trắng/Bài tập), KHÔNG chung ô với pill "Địa lí 10" cạnh tiêu đề (từng thử, bị nhận
 * xét bảng màu "bay" xa khỏi nút Bút vừa bấm, sang tận cạnh tiêu đề — xem lịch sử
 * review). `o.getPenSlot` (Shell.js cấp, đọc từ `header.getPenSlot()`) trả về ô
 * trống đó — `ensureMounted()` CHUYỂN THẲNG node `palette` vào đó (một DOM node chỉ
 * có MỘT cha; appendChild vào nơi khác tự gỡ khỏi cha cũ, không cần tự remove
 * trước).
 *
 * Vì sao không chuyển ngay lúc tạo: PenLayer được tạo TRƯỚC Header (Header cần
 * callback trỏ vào PenLayer.setActive ngay lúc dựng, xem Shell.js) nên `getPenSlot`
 * chưa gọi được lúc này. Mount tạm vào `o.stage` (ẩn, vì chưa `.is-open`) rồi CHUYỂN
 * vào slot ở lần setActive(true) ĐẦU TIÊN — cùng lối headerRef đã dùng cho
 * onToggleWhiteboard/onToggleExercise trong Shell.js. */

import { createColorSwatches, createDrawCanvas } from './DrawCanvas.js';

/**
 * @param {Object} o
 * @param {HTMLElement} o.stage
 * @param {(k: string, v?: any) => string} o.t
 * @param {() => HTMLElement | null | undefined} [o.getPenSlot] Ô trong Header để mount
 *   bảng màu vào. Thiếu thì bảng màu ở lại o.stage (ẩn, vì chưa .is-open) — không vẽ
 *   được lên đó nhưng cũng không lỗi.
 */
export function createPenLayer(o) {
  const draw = createDrawCanvas({ mount: o.stage, className: 'ol-pen' });

  const palette = document.createElement('div');
  palette.className = 'ol-pen-palette';
  const { swatchesEl, customEl } = createColorSwatches({ onChange: (c) => draw.setColor(c) });
  const sep = document.createElement('span');
  sep.className = 'ol-pen-palette__sep';
  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'ol-pen-palette__clear';
  palette.append(swatchesEl, customEl, sep, clearBtn);
  o.stage.appendChild(palette);

  const onClearClick = () => draw.clear();
  clearBtn.addEventListener('click', onClearClick);

  /** Chuyển `palette` vào slot của Header nếu có VÀ chưa nằm ở đó — gọi lại nhiều lần
   * (mỗi lần bật bút) vô hại, appendChild một node đã đúng cha không làm gì cả. */
  function ensureMounted() {
    const slot = o.getPenSlot?.();
    if (slot && palette.parentElement !== slot) slot.appendChild(palette);
  }

  function retranslate() {
    const clearText = o.t('pen.clear');
    clearBtn.textContent = clearText;
    clearBtn.title = clearText;
    palette.setAttribute('aria-label', o.t('pen.palette'));
  }
  retranslate();

  let active = false;

  return {
    retranslate,
    /** @param {boolean} on */
    setActive(on) {
      active = on;
      draw.canvas.classList.toggle('is-active', on);
      if (on) ensureMounted();
      palette.classList.toggle('is-open', on);
      if (!on) draw.clear(); // tắt bút = trang trắng mới lần bật kế tiếp, xem đầu file
    },
    isActive: () => active,
    dispose() {
      draw.dispose();
      clearBtn.removeEventListener('click', onClearClick);
      palette.remove();
    },
  };
}
